import React, { useState } from "react";
import { periodWindow } from "./helpers";

/**
 * Draws the composite score as a progress ring.
 *
 * @param {{score: number}} props - The composite score, 0-100.
 * @returns {JSX.Element} An SVG ring.
 */
const ScoreRing = ({ score }) => {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.max(0, Math.min(100, score)) / 100);

  return (
    <svg
      width="130"
      height="130"
      viewBox="0 0 130 130"
      role="img"
      aria-label={`Overall score ${score} out of 100`}
    >
      <circle
        cx="65"
        cy="65"
        r={radius}
        fill="none"
        stroke="var(--surface-3)"
        strokeWidth="11"
      />
      <circle
        cx="65"
        cy="65"
        r={radius}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="11"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform="rotate(-90 65 65)"
      />
      <text
        x="65"
        y="63"
        textAnchor="middle"
        fontSize="30"
        fontWeight="800"
        fill="var(--ink)"
      >
        {score}
      </text>
      <text
        x="65"
        y="83"
        textAnchor="middle"
        fontSize="9.5"
        fontWeight="700"
        letterSpacing="1"
        fill="var(--ink-3)"
      >
        OUT OF 100
      </text>
    </svg>
  );
};

/**
 * Shows the composite ESG position and the confidence behind it.
 *
 * @param {object} props - The payload, current period and compare mode.
 * @returns {JSX.Element|null} The card, or nothing until the section completes.
 *
 * @remarks
 * The confidence breakdown is collapsed by default. A single number invites
 * false precision, so the four factors are one click away rather than buried —
 * a reviewer can see which part of the analysis is weakest.
 */
const OverallPositionCard = ({ payload, period, compareMode }) => {
  const [showFactors, setShowFactors] = useState(false);

  if (!payload?.you?.length) return null;

  const { to } = periodWindow(payload.years, period);
  const mine = payload.you[to];
  const comparison =
    compareMode === "leader" ? payload.leader[to] : payload.peerAvg[to];
  const delta = mine - comparison;
  const comparisonLabel =
    compareMode === "leader"
      ? `sector leader (${payload.leaderName})`
      : "peer average";

  const bars = [
    { label: "You", value: mine, className: "is-you" },
    { label: "Peer average", value: payload.peerAvg[to], className: "is-peer" },
    { label: "Sector leader", value: payload.leader[to], className: "is-leader" },
  ];

  return (
    <section className="aica-card">
      <div className="aica-card-head">
        <div>
          <h2>Overall ESG position</h2>
          <p className="aica-sub">
            Composite score across every benchmark KPI, {payload.years[to]}.
          </p>
        </div>
        <button
          type="button"
          className="aica-confidence-chip"
          onClick={() => setShowFactors((current) => !current)}
          aria-expanded={showFactors}
        >
          AI confidence {payload.confidence.overall}% &middot;{" "}
          {payload.confidence.label}
        </button>
      </div>

      <div className="aica-overall">
        <div className="aica-ring-wrap">
          <ScoreRing score={mine} />
          <span
            className={`aica-delta ${delta >= 0 ? "is-ahead" : "is-behind"}`}
          >
            {delta >= 0 ? "▲ +" : "▼ "}
            {Math.abs(delta).toFixed(0)} pts vs {comparisonLabel}
          </span>
        </div>

        <div className="aica-bars">
          {bars.map((bar) => (
            <div className="aica-bar-row" key={bar.label}>
              <span className="aica-bar-label">{bar.label}</span>
              <span className="aica-bar-track">
                <span
                  className={`aica-bar-fill ${bar.className}`}
                  style={{ width: `${Math.max(0, Math.min(100, bar.value))}%` }}
                />
              </span>
              <span className="aica-bar-value">{bar.value}</span>
            </div>
          ))}
        </div>
      </div>

      {showFactors && (
        <div className="aica-factors">
          <div className="aica-section-label">How this confidence is derived</div>
          {payload.confidence.factors.map((factor) => (
            <div className="aica-factor" key={factor.name}>
              <div className="aica-factor-head">
                <span className="aica-factor-name">{factor.name}</span>
                <span className="aica-factor-score">{factor.score}</span>
              </div>
              <span className="aica-bar-track">
                <span
                  className="aica-bar-fill is-you"
                  style={{
                    width: `${Math.max(0, Math.min(100, factor.score))}%`,
                  }}
                />
              </span>
              <p className="aica-factor-note">{factor.note}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default OverallPositionCard;
